/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        if (!this.section) return;

        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons')
          || this.section.querySelector('.offers-buy-card-container')
          || this.section.querySelector('.offers-buy-buttons-block');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        // Buy Now button in sticky bar
        this.buyNowBtn = this.querySelector('[data-sticky-buy-now]');

        // Pre-book warning element
        this.prebookWarning = this.querySelector('[data-sticky-prebook-warning]');

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        // Listen for variant change events (bubbles from variant-picker through DOM)
        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Find the variant ID input from any form type (buy-buttons or offers-buy-buttons).
       * @returns {string} The variant ID value, or empty string.
       */
      getVariantId() {
        const el = this.section.querySelector('product-form buy-buttons input[name="id"]')
          || this.section.querySelector('product-form .js-product-form input[name="id"]')
          || this.section.querySelector('.offers-buy-form input[name="id"]')
          || this.form?.querySelector('input[name="id"]');
        return el ? el.value : '';
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId = this.getVariantId();
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Sync sticky form variant ID with main form
        const ownIdInput = this.form?.querySelector('input[name="id"]');
        if (ownIdInput && ownIdInput.value !== variantId) {
          ownIdInput.value = variantId;
        }

        // Validate main form
        if (this.mainProductForm) {
          const customFormValid = this.mainProductForm.validate();
          if (!customFormValid || !this.mainProductForm.form?.reportValidity()) {
            if (!customFormValid) {
              theme.scrollToRevealElement(this.mainProductForm);
            } else {
              const input = Array.from(this.mainProductForm.form.elements).find(
                (el) => !el.checkValidity()
              );
              if (input) setTimeout(() => theme.scrollToRevealElement(input), 100);
            }
            return;
          }

          // Copy data from main form
          this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
          const formData = new FormData(this.mainProductForm.form);
          for (const p of formData) {
            if (!this.form.querySelector(`[name="${p[0]}"]`)) {
              const input = document.createElement('input');
              input.name = p[0];
              input.value = p[1];
              input.hidden = true;
              input.setAttribute('data-copied', '');
              this.form.append(input);
            }
          }
        }

        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       * On mobile (<=767px): show sticky bar as soon as user scrolls at all,
       * since the main buy buttons are hidden on mobile.
       * On desktop: show only after the buy-buttons area scrolls out of view.
       */
      static handleScroll() {
        const isMobile = window.innerWidth <= 767;

        if (isMobile) {
          this.classList.toggle('sticky-atc--out', window.scrollY <= 0);
        } else {
          const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
          this.classList.toggle(
            'sticky-atc--out',
            this.buyButtons && this.buyButtons.getBoundingClientRect().bottom > topOffset
          );
        }

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          if (this.variantTitle) this.variantTitle.textContent = evt.detail.variant.title;
          if (this.variantContainer) this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          if (this.variantContainer) this.variantContainer.hidden = true;
          if (this.variantTitle) this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant, evt.detail.formatted);
        this.updateBuyNowButton(evt.detail.variant, evt.detail.formatted);
        this.updatePrebookWarning(evt.detail.variant, evt.detail.formatted);
        this.setErrorMsgState();
      }

      /**
       * Updates the image.
       */
      updateImage() {
        if (!this.imageContainer) return;
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Determine if current variant is in pre-book state.
       * @param {object} variant
       * @param {object} [formatted]
       * @returns {boolean}
       */
      isPreBook(variant, formatted) {
        if (!variant || !variant.available) return false;
        const f = formatted && formatted[variant.id] ? formatted[variant.id] : {};
        const qty = f.inventoryQuantity != null ? f.inventoryQuantity : 0;
        const policy = f.inventoryPolicy || 'deny';
        const tracked = f.inventoryTracked != null ? f.inventoryTracked : false;
        return tracked && qty <= 0 && policy === 'continue';
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       * @param {object} [formatted] - Formatted variant data including inventory info.
       */
      updateAddToCartButton(variant, formatted) {
        if (!this.submitBtn) return;

        if (!variant) {
          this.submitBtn.textContent = this.submitBtn.dataset.unavailableText || theme.strings.noVariant;
          this.submitBtn.disabled = true;
          this.submitBtn.classList.remove('btn--pre-book', 'btn--out-of-stock');
          return;
        }

        const variantFormatted = formatted && formatted[variant.id] ? formatted[variant.id] : {};
        const inventoryQty = variantFormatted.inventoryQuantity != null ? variantFormatted.inventoryQuantity : 0;
        const inventoryPolicy = variantFormatted.inventoryPolicy || 'deny';
        const inventoryTracked = variantFormatted.inventoryTracked != null ? variantFormatted.inventoryTracked : false;

        if (!variant.available) {
          this.submitBtn.disabled = true;
          this.submitBtn.textContent = theme.strings.outOfStock;
          this.submitBtn.classList.remove('btn--pre-book');
          this.submitBtn.classList.add('btn--out-of-stock');
        } else if (!inventoryTracked || inventoryQty > 0) {
          this.submitBtn.disabled = false;
          this.submitBtn.textContent = this.submitBtn.dataset.addToCartText;
          this.submitBtn.classList.remove('btn--pre-book', 'btn--out-of-stock');
        } else if (inventoryQty <= 0 && inventoryPolicy === 'continue') {
          this.submitBtn.disabled = false;
          this.submitBtn.textContent = theme.strings.preBook;
          this.submitBtn.classList.add('btn--pre-book');
          this.submitBtn.classList.remove('btn--out-of-stock');
        } else {
          this.submitBtn.disabled = true;
          this.submitBtn.textContent = theme.strings.outOfStock;
          this.submitBtn.classList.remove('btn--pre-book');
          this.submitBtn.classList.add('btn--out-of-stock');
        }
      }

      /**
       * Updates the sticky Buy Now button state based on variant availability.
       * @param {object} variant - Current variant object.
       * @param {object} [formatted] - Formatted variant data including inventory info.
       */
      updateBuyNowButton(variant, formatted) {
        if (!this.buyNowBtn) return;

        if (!variant || !variant.available || this.isPreBook(variant, formatted)) {
          this.buyNowBtn.disabled = true;
          this.buyNowBtn.style.display = 'none';
        } else {
          this.buyNowBtn.disabled = false;
          this.buyNowBtn.style.display = '';
        }
      }

      /**
       * Shows or hides the pre-book / made-to-order warning in the sticky bar.
       * @param {object} variant
       * @param {object} [formatted]
       */
      updatePrebookWarning(variant, formatted) {
        if (!this.prebookWarning) return;
        this.prebookWarning.hidden = !this.isPreBook(variant, formatted);
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
